/**
 * Login Page
 *
 * Editorial nameplate with a single Okta sign-in button.
 */

import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { css } from "styled-system/css";
import { useAuth } from "../../lib/auth";

export function LoginPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const redirectTo = searchParams.get("redirectTo") || "/";
  const error = searchParams.get("error");

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, redirectTo]);

  const handleLogin = () => {
    login(redirectTo);
  };

  const dateline = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <main className={styles.page}>
      <div className={styles.column}>
        <div className={styles.topRule}>
          <span>Autura Engineering</span>
          <span className={styles.mono}>{dateline}</span>
        </div>

        <h1 className={styles.nameplate}>Aura</h1>

        <p className={styles.line}>
          How engineering at Autura ships: pull requests, reviews, Jira work and
          AI spend, counted the same way for every team.
        </p>

        {error && (
          <div className={styles.error} role="alert">
            <span aria-hidden className={styles.errorGlyph}>
              ▲
            </span>
            <span>{safeDecode(error)}</span>
          </div>
        )}

        <button
          type="button"
          className={styles.button}
          onClick={handleLogin}
          disabled={isLoading}
        >
          {isLoading ? "Loading…" : "Sign in with Okta"}
        </button>

        <p className={styles.footer}>Use your Autura company account.</p>
      </div>
    </main>
  );
}

// A malformed error param would throw inside decodeURIComponent and blank the
// page; fall back to the raw text.
function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

const styles = {
  page: css({
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bg: "ed.paper",
    color: "ed.ink",
    px: "16px",
    py: "48px",
  }),
  column: css({
    width: "100%",
    maxWidth: "440px",
    display: "flex",
    flexDirection: "column",
  }),
  topRule: css({
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    borderTopWidth: "3px",
    borderTopStyle: "solid",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.ink",
    py: "6px",
    fontFamily: "sans",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "ed.ink2",
  }),
  mono: css({
    fontFamily: "mono",
    fontWeight: 400,
    letterSpacing: "0.02em",
    textTransform: "none",
    fontVariantNumeric: "tabular-nums",
  }),
  nameplate: css({
    fontFamily: "serif",
    fontWeight: 700,
    fontSize: { base: "72px", sm: "96px" },
    lineHeight: 1,
    letterSpacing: "-0.03em",
    textAlign: "center",
    m: 0,
    py: "22px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.ink",
  }),
  line: css({
    fontFamily: "serif",
    fontSize: "17px",
    lineHeight: 1.5,
    color: "ed.ink2",
    textAlign: "center",
    m: 0,
    mt: "20px",
    mb: "28px",
    textWrap: "balance",
  }),
  error: css({
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
    mb: "16px",
    px: "12px",
    py: "10px",
    fontFamily: "sans",
    fontSize: "13.5px",
    lineHeight: 1.45,
    color: "ed.ink",
    bg: "ed.cautionTint",
    borderTopWidth: "2px",
    borderTopStyle: "solid",
    borderColor: "ed.caution",
  }),
  errorGlyph: css({ color: "ed.caution", fontSize: "11px", pt: "3px" }),
  button: css({
    width: "100%",
    px: "16px",
    py: "12px",
    fontFamily: "sans",
    fontSize: "14px",
    fontWeight: 600,
    letterSpacing: "0.02em",
    color: "ed.paper",
    bg: "ed.ink",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: "0",
    cursor: "pointer",
    transition: "background 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { bg: "ed.ink2", borderColor: "ed.ink2" },
    _focusVisible: {
      outline: "2px solid",
      outlineColor: "ed.ink",
      outlineOffset: "2px",
    },
    _disabled: { opacity: 0.5, cursor: "not-allowed" },
  }),
  footer: css({
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink3",
    textAlign: "center",
    m: 0,
    mt: "12px",
  }),
};
